import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUrlState } from "@/hooks/use-url-state";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { PartnerPayment, RctiLineItem } from "@shared/schema";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending: {
    label: "Pending",
    className: "bg-amber-100 text-amber-700 border-amber-300",
  },
  approved: {
    label: "Approved",
    className: "bg-blue-100 text-blue-700 border-blue-300",
  },
  processing: {
    label: "Processing",
    className: "bg-purple-100 text-purple-700 border-purple-300",
  },
  completed: {
    label: "Paid",
    className: "bg-green-100 text-green-700 border-green-300",
  },
  cancelled: {
    label: "Cancelled",
    className: "bg-slate-100 text-slate-500 border-slate-200",
  },
  failed: {
    label: "Failed",
    className: "bg-red-100 text-red-700 border-red-300",
  },
};

const STATUS_FILTER_OPTIONS = [
  { value: "none", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
  { value: "failed", label: "Failed" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

const formatPeriod = (month: number | null, year: number | null): string => {
  if (!month || !year) return "-";
  return `${MONTH_NAMES[month - 1]} ${year}`;
};

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

function StatusBadge({ status, id }: { status: string; id: string }) {
  const config = STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`status-badge-${id}`}
    >
      {config.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Line Items Row (expanded)
// ---------------------------------------------------------------------------

interface LineItemsRowProps {
  rctiId: string;
  colSpan: number;
}

function LineItemsRow({ rctiId, colSpan }: LineItemsRowProps) {
  const { data, isLoading } = useQuery<RctiLineItem[]>({
    queryKey: ["/api/commissions/rcti", rctiId, "line-items"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/commissions/rcti/${rctiId}/line-items`
      );
      return response.json();
    },
  });

  const lineItems = Array.isArray(data) ? data : [];

  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="p-0 bg-muted/30">
        <div
          data-testid={`line-items-${rctiId}`}
          className="p-4 border-t"
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Loading line items...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground">
                      Account #
                    </th>
                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground">
                      Deal Reference
                    </th>
                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground">
                      Type
                    </th>
                    <th className="text-right py-2 pr-4 font-medium text-muted-foreground">
                      Amount
                    </th>
                    <th className="text-right py-2 pr-4 font-medium text-muted-foreground">
                      GST
                    </th>
                    <th className="text-right py-2 font-medium text-muted-foreground">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-4 text-center text-sm text-muted-foreground"
                      >
                        No line items found for this RCTI.
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((item) => (
                      <tr key={item.id} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-mono text-xs">
                          {item.accountNumber ?? "-"}
                        </td>
                        <td className="py-2 pr-4">
                          {item.dealReference ?? "-"}
                        </td>
                        <td className="py-2 pr-4 capitalize">
                          {item.commissionType ?? "-"}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          {formatCurrency(item.commissionAmount)}
                        </td>
                        <td className="py-2 pr-4 text-right text-muted-foreground">
                          {formatCurrency(item.gstAmount ?? null)}
                        </td>
                        <td className="py-2 text-right font-medium">
                          {formatCurrency(item.totalAmount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Xero Inline Edit Section
// ---------------------------------------------------------------------------

interface XeroSectionProps {
  rcti: PartnerPayment;
  onSave: (field: string, value: string) => void;
}

function XeroSection({ rcti, onSave }: XeroSectionProps) {
  const [xeroNumber, setXeroNumber] = useState(rcti.xeroRctiNumber ?? "");
  const [editingField, setEditingField] = useState<string | null>(null);

  const handleSave = (field: string, value: string) => {
    onSave(field, value);
    setEditingField(null);
  };

  return (
    <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t">
      {/* Xero RCTI Number */}
      <div
        className="flex flex-col gap-1 min-w-[180px]"
        data-testid={`xero-rcti-number-${rcti.id}`}
        onClick={() => setEditingField("xeroRctiNumber")}
      >
        <span className="text-xs text-muted-foreground font-medium">
          Xero RCTI #
        </span>
        {editingField === "xeroRctiNumber" ? (
          <Input
            autoFocus
            value={xeroNumber}
            onChange={(e) => setXeroNumber(e.target.value)}
            onBlur={() => handleSave("xeroRctiNumber", xeroNumber)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave("xeroRctiNumber", xeroNumber);
              if (e.key === "Escape") setEditingField(null);
            }}
            className="h-7 text-sm"
            placeholder="Enter Xero RCTI #"
          />
        ) : (
          <span className="text-sm cursor-pointer hover:text-primary transition-colors">
            {rcti.xeroRctiNumber ?? (
              <span className="text-muted-foreground italic">Not set</span>
            )}
          </span>
        )}
      </div>

      {/* Xero Generated Date */}
      <div className="flex flex-col gap-1 min-w-[180px]">
        <span className="text-xs text-muted-foreground font-medium">
          Generated Date
        </span>
        <span className="text-sm">
          {formatDate(rcti.xeroGeneratedDate)}
        </span>
      </div>

      {/* RCTI Sent Date */}
      <div className="flex flex-col gap-1 min-w-[180px]">
        <span className="text-xs text-muted-foreground font-medium">
          Sent Date
        </span>
        <span className="text-sm">
          {formatDate(rcti.rctiSentDate)}
        </span>
      </div>

      {/* Payment Received Date */}
      <div className="flex flex-col gap-1 min-w-[180px]">
        <span className="text-xs text-muted-foreground font-medium">
          Payment Received
        </span>
        <span className="text-sm">
          {formatDate(rcti.paymentReceivedDate)}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generate Dialog
// ---------------------------------------------------------------------------

interface GenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (month: number, year: number) => Promise<void>;
  isGenerating: boolean;
}

function GenerateDialog({
  open,
  onOpenChange,
  onGenerate,
  isGenerating,
}: GenerateDialogProps) {
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));

  const handleGenerate = async () => {
    await onGenerate(parseInt(month, 10), parseInt(year, 10));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-generate-rcti">
        <DialogHeader>
          <DialogTitle>Generate Monthly RCTI Records</DialogTitle>
          <DialogDescription>
            This will create RCTI records for all active partners with matched
            transactions in the selected period.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 py-2">
          {/* Month picker */}
          <div className="flex-1">
            <label className="text-sm font-medium mb-1 block">Month</label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="select-generate-month">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((name, idx) => (
                  <SelectItem key={idx + 1} value={String(idx + 1)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year picker */}
          <div className="flex-1">
            <label className="text-sm font-medium mb-1 block">Year</label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="select-generate-year">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            data-testid="button-dialog-generate"
          >
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Mark as Paid Dialog
// ---------------------------------------------------------------------------

interface MarkPaidDialogProps {
  open: boolean;
  rcti: PartnerPayment | null;
  onOpenChange: (open: boolean) => void;
  onMarkPaid: (id: string, receivedDate: string, notes: string) => Promise<void>;
  isLoading: boolean;
}

function MarkPaidDialog({
  open,
  rcti,
  onOpenChange,
  onMarkPaid,
  isLoading,
}: MarkPaidDialogProps) {
  const [receivedDate, setReceivedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [notes, setNotes] = useState("");

  const handleSubmit = async () => {
    if (!rcti) return;
    await onMarkPaid(rcti.id, receivedDate, notes);
    onOpenChange(false);
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-mark-paid">
        <DialogHeader>
          <DialogTitle>Mark as Paid</DialogTitle>
          <DialogDescription>
            Record the payment received date for{" "}
            {rcti?.internalReference ?? "this RCTI"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Payment Received Date
            </label>
            <Input
              type="date"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
              data-testid="input-payment-received-date"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Notes</label>
            <Input
              placeholder="Optional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="input-mark-paid-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !receivedDate}>
            {isLoading ? "Saving..." : "Mark as Paid"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function CommissionRctiPageContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Filters — URL persisted
  const [filterYear, setFilterYear] = useUrlState("year", "none");
  const [filterMonth, setFilterMonth] = useUrlState("month", "none");
  const [filterStatus, setFilterStatus] = useUrlState("status", "none");

  // Expanded row state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Dialog state
  const [generateOpen, setGenerateOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidRcti, setMarkPaidRcti] = useState<PartnerPayment | null>(null);

  // Build query params
  const queryParams = new URLSearchParams();
  if (filterYear !== "none") queryParams.set("year", filterYear);
  if (filterMonth !== "none") queryParams.set("month", filterMonth);
  if (filterStatus !== "none") queryParams.set("paymentStatus", filterStatus);

  const { data, isLoading } = useQuery<PartnerPayment[]>({
    queryKey: [
      "/api/commissions/rcti",
      filterYear,
      filterMonth,
      filterStatus,
    ],
    queryFn: async () => {
      const url = `/api/commissions/rcti${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const rctiList = Array.isArray(data) ? data : [];

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: async ({ month, year }: { month: number; year: number }) => {
      const response = await apiRequest("POST", "/api/commissions/rcti/generate", {
        month,
        year,
      });
      return response.json();
    },
    onSuccess: (result) => {
      toast({
        title: "RCTI Generation Complete",
        description: `Generated ${result.generated} RCTI record${result.generated !== 1 ? "s" : ""}${result.skipped > 0 ? `, skipped ${result.skipped}` : ""}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/rcti"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Generation Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Mark as paid mutation
  const markPaidMutation = useMutation({
    mutationFn: async ({
      id,
      receivedDate,
      notes,
    }: {
      id: string;
      receivedDate: string;
      notes: string;
    }) => {
      const response = await apiRequest("PATCH", `/api/commissions/rcti/${id}`, {
        paymentStatus: "completed",
        paymentReceivedDate: receivedDate,
        ...(notes ? { notes } : {}),
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "RCTI Updated",
        description: "Payment marked as received.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/rcti"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Xero field save mutation
  const xeroUpdateMutation = useMutation({
    mutationFn: async ({
      id,
      field,
      value,
    }: {
      id: string;
      field: string;
      value: string;
    }) => {
      const response = await apiRequest("PATCH", `/api/commissions/rcti/${id}`, {
        [field]: value,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/rcti"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Save Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleGenerate = async (month: number, year: number) => {
    await generateMutation.mutateAsync({ month, year });
  };

  const handleMarkPaid = async (
    id: string,
    receivedDate: string,
    notes: string
  ) => {
    await markPaidMutation.mutateAsync({ id, receivedDate, notes });
  };

  const handleXeroSave = (id: string, field: string, value: string) => {
    xeroUpdateMutation.mutate({ id, field, value });
  };

  const COL_COUNT = 8;

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Header row: title + Generate button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">RCTI Management</h2>
          <p className="text-sm text-muted-foreground">
            Recipient Created Tax Invoice records for referral partner payments
          </p>
        </div>
        <Button
          onClick={() => setGenerateOpen(true)}
          data-testid="button-generate-rcti"
        >
          <Plus className="mr-2 h-4 w-4" />
          Generate Monthly Records
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          {/* Month filter */}
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger
              className="w-[150px]"
              data-testid="select-filter-month"
            >
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">All Months</SelectItem>
              {MONTH_NAMES.map((name, idx) => (
                <SelectItem key={idx + 1} value={String(idx + 1)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Year filter */}
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger
              className="w-[120px]"
              data-testid="select-filter-year"
            >
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">All Years</SelectItem>
              {YEAR_OPTIONS.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status filter */}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger
              className="w-[160px]"
              data-testid="select-filter-status"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(filterYear !== "none" || filterMonth !== "none" || filterStatus !== "none") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterYear("none");
                setFilterMonth("none");
                setFilterStatus("none");
              }}
              className="text-muted-foreground"
            >
              Clear filters
            </Button>
          )}

          {rctiList.length > 0 && (
            <span className="text-sm text-muted-foreground ml-auto">
              {rctiList.length} record{rctiList.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </Card>

      {/* RCTI Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Partner</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Internal Ref</TableHead>
              <TableHead className="text-right">Items</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: COL_COUNT }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rctiList.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COL_COUNT}
                  className="py-16 text-center text-muted-foreground"
                >
                  <div className="flex flex-col items-center space-y-3">
                    <FileText className="h-10 w-10 text-muted-foreground/40" />
                    <p className="text-base font-medium">
                      No RCTI records found
                    </p>
                    <p className="text-sm">
                      Generate RCTI records for a period to get started.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGenerateOpen(true)}
                      data-testid="button-generate-rcti-empty"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Generate monthly records
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rctiList.flatMap((rcti) => {
                const isExpanded = expandedIds.has(rcti.id);
                const rows = [
                  <TableRow
                    key={rcti.id}
                    data-testid={`row-rcti-${rcti.id}`}
                    className="cursor-pointer hover:bg-muted/30"
                  >
                    {/* Expand toggle */}
                    <TableCell className="w-8 px-2">
                      <button
                        type="button"
                        onClick={() => toggleExpand(rcti.id)}
                        data-testid={`button-expand-${rcti.id}`}
                        className="flex items-center justify-center p-1 rounded hover:bg-muted transition-colors"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    </TableCell>

                    {/* Partner — ID only (no JOIN in this API, show truncated ID) */}
                    <TableCell className="font-medium text-sm">
                      <span
                        className="text-muted-foreground"
                        title={rcti.partnerId}
                      >
                        {rcti.partnerId.slice(0, 8)}…
                      </span>
                    </TableCell>

                    {/* Period */}
                    <TableCell>
                      {formatPeriod(rcti.paymentMonth, rcti.paymentYear)}
                    </TableCell>

                    {/* Internal reference */}
                    <TableCell className="font-mono text-sm">
                      {rcti.internalReference ?? "-"}
                    </TableCell>

                    {/* Line items count */}
                    <TableCell className="text-right">
                      {rcti.totalLineItems ?? 0}
                    </TableCell>

                    {/* Total payment */}
                    <TableCell className="text-right font-medium">
                      {formatCurrency(rcti.totalPaymentAmount)}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <StatusBadge
                        status={rcti.paymentStatus ?? "pending"}
                        id={rcti.id}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      {(rcti.paymentStatus === "pending" ||
                        rcti.paymentStatus === "approved") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMarkPaidRcti(rcti);
                            setMarkPaidOpen(true);
                          }}
                          data-testid={`button-mark-paid-${rcti.id}`}
                        >
                          Mark as Paid
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>,
                ];

                if (isExpanded) {
                  rows.push(
                    <TableRow key={`${rcti.id}-xero`}>
                      <TableCell colSpan={COL_COUNT} className="p-0 bg-muted/10">
                        <div className="px-6 pb-4 pt-2">
                          <XeroSection
                            rcti={rcti}
                            onSave={(field, value) =>
                              handleXeroSave(rcti.id, field, value)
                            }
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                  rows.push(
                    <LineItemsRow
                      key={`${rcti.id}-items`}
                      rctiId={rcti.id}
                      colSpan={COL_COUNT}
                    />
                  );
                }

                return rows;
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Generate Dialog */}
      <GenerateDialog
        open={generateOpen}
        onOpenChange={setGenerateOpen}
        onGenerate={handleGenerate}
        isGenerating={generateMutation.isPending}
      />

      {/* Mark as Paid Dialog */}
      <MarkPaidDialog
        open={markPaidOpen}
        rcti={markPaidRcti}
        onOpenChange={setMarkPaidOpen}
        onMarkPaid={handleMarkPaid}
        isLoading={markPaidMutation.isPending}
      />
    </div>
  );
}
